import type { WonderId, WonderSide } from '@sw/engine';
import { useId } from 'react';
import { wonderArtUrl } from '../assets/manifest';
import { seeded } from '../lib/hash';

function Silhouette({ id, ink }: { id: WonderId; ink: string }) {
  switch (id) {
    case 'gizah':
      return (
        <g fill={ink}>
          <path d="M80 100 L150 30 L220 100Z" />
          <path d="M190 100 L235 58 L280 100Z" opacity="0.85" />
          <path d="M30 100 L62 70 L94 100Z" opacity="0.8" />
          <path d="M150 30 L220 100 H190Z" fill="#000" opacity="0.25" />
        </g>
      );
    case 'rhodos':
      return (
        <g fill={ink}>
          <rect x="105" y="92" width="30" height="8" />
          <rect x="165" y="92" width="30" height="8" />
          <path d="M118 92 L140 52 H160 L182 92 H170 L152 62 H148 L130 92Z" />
          <rect x="140" y="28" width="20" height="26" rx="4" />
          <circle cx="150" cy="22" r="7" />
          <path d="M160 32 L178 8 L182 10 L166 36Z" />
          <circle cx="180" cy="7" r="4" fill="#ffcf6a" />
          <path d="M0 100 Q75 94 150 100 T300 100" stroke={ink} fill="none" />
        </g>
      );
    case 'alexandria':
      return (
        <g fill={ink}>
          <rect x="125" y="70" width="50" height="30" />
          <rect x="135" y="42" width="30" height="28" />
          <rect x="142" y="20" width="16" height="22" />
          <circle cx="150" cy="15" r="6" fill="#ffd36e" />
          <path d="M150 15 L40 0 L40 6Z M150 15 L260 0 L260 6Z" fill="#ffe7a0" opacity="0.35" />
          <path d="M0 100 Q60 92 120 100 M180 100 Q240 92 300 100" stroke={ink} fill="none" />
        </g>
      );
    case 'ephesos':
      return (
        <g fill={ink}>
          <path d="M70 46 L150 22 L230 46Z" />
          <rect x="70" y="46" width="160" height="5" />
          {Array.from({ length: 9 }, (_, i) => (
            <rect key={i} x={76 + i * 18} y="53" width="7" height="40" />
          ))}
          <rect x="62" y="93" width="176" height="7" />
        </g>
      );
    case 'babylon':
      return (
        <g fill={ink}>
          <rect x="70" y="80" width="160" height="20" />
          <rect x="90" y="62" width="120" height="18" />
          <rect x="110" y="44" width="80" height="18" />
          <rect x="130" y="28" width="40" height="16" />
          {[76, 96, 116, 136, 156, 176, 196, 216].map((x, i) => (
            <circle key={i} cx={x} cy={80 - (i % 3) * 2} r="6" fill="#2f5a2a" />
          ))}
          {[100, 125, 150, 175, 200].map((x, i) => (
            <circle key={i} cx={x} cy="61" r="5" fill="#2f5a2a" />
          ))}
          {[120, 150, 180].map((x, i) => (
            <circle key={i} cx={x} cy="43" r="5" fill="#2f5a2a" />
          ))}
        </g>
      );
    case 'olympia':
      return (
        <g fill={ink}>
          <path d="M80 44 L150 22 L220 44Z" />
          <rect x="80" y="44" width="140" height="5" />
          {Array.from({ length: 6 }, (_, i) => (
            <rect key={i} x={86 + i * 25} y="51" width="7" height="42" />
          ))}
          <path d="M140 92 V72 Q150 60 160 72 V92Z" fill="#c9a24a" opacity="0.8" />
          <circle cx="150" cy="64" r="5" fill="#c9a24a" opacity="0.8" />
          <rect x="72" y="93" width="156" height="7" />
        </g>
      );
    case 'halikarnassos':
      return (
        <g fill={ink}>
          <rect x="100" y="70" width="100" height="30" />
          {Array.from({ length: 7 }, (_, i) => (
            <rect key={i} x={104 + i * 15} y="48" width="5" height="22" />
          ))}
          <rect x="98" y="45" width="104" height="4" />
          <path d="M104 45 L150 20 L196 45Z" />
          <path d="M140 20 h20 l-3 -8 h-14z" />
          <rect x="94" y="96" width="112" height="4" />
        </g>
      );
  }
}

/** Ilustración del tablero de maravilla (arte generado o escena procedural día/noche). */
export function WonderArt({ wonder, side }: { wonder: WonderId; side: WonderSide }) {
  const uid = useId().replace(/:/g, '');
  const url = wonderArtUrl(wonder, side);
  if (url) return <img src={url} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
  const night = side === 'night';
  const rnd = seeded(wonder + side);
  return (
    <svg viewBox="0 0 300 100" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" style={{ display: 'block' }} aria-hidden="true">
      <defs>
        <linearGradient id={`ws${uid}`} x1="0" y1="0" x2="0" y2="1">
          {night ? (
            <>
              <stop offset="0" stopColor="#0b1030" />
              <stop offset="0.7" stopColor="#24305e" />
              <stop offset="1" stopColor="#4b3f6b" />
            </>
          ) : (
            <>
              <stop offset="0" stopColor="#7fb6e3" />
              <stop offset="0.65" stopColor="#f2d9a8" />
              <stop offset="1" stopColor="#e8a45e" />
            </>
          )}
        </linearGradient>
        <radialGradient id={`wv${uid}`} cx="0.5" cy="0.4" r="0.8">
          <stop offset="0.55" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.5" />
        </radialGradient>
      </defs>
      <rect width="300" height="100" fill={`url(#ws${uid})`} />
      {night
        ? Array.from({ length: 40 }, (_, i) => (
            <circle key={i} cx={rnd() * 300} cy={rnd() * 55} r={0.4 + rnd() * 0.9} fill="#fff" opacity={0.4 + rnd() * 0.6} />
          ))
        : null}
      <circle cx={night ? 245 : 60} cy={night ? 20 : 28} r={night ? 9 : 13} fill={night ? '#f4f1de' : '#fff3c9'} opacity="0.9" />
      <path d="M0 88 Q80 78 160 86 T300 84 V100 H0Z" fill="#000" opacity="0.25" />
      <Silhouette id={wonder} ink={night ? '#080a18' : '#3a2a17'} />
      <rect width="300" height="100" fill={`url(#wv${uid})`} />
    </svg>
  );
}
